package com.schoolconnect.app.repository.classconversations

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.ClassConversationReplyCreateRequest
import com.schoolconnect.app.datasource.network.request.ClassConversationCreateRequest
import com.schoolconnect.app.datasource.network.request.ClassConversationUpdateRequest
import com.schoolconnect.app.datasource.network.response.ClassConversationListResponse
import com.schoolconnect.app.datasource.network.response.ClassConversationReplyResponse
import com.schoolconnect.app.datasource.network.response.ClassConversationResponse
import com.schoolconnect.app.datasource.network.response.RecordListResponse
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey
import kotlinx.coroutines.CoroutineScope

data class ClassConversationsPageResult(
    val items: List<ClassConversationListResponse>,
    val totalCount: Int
)

object ClassConversationsRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ClassConversationsRepository"
    private val pageCache = RepositoryCache<ClassConversationsPageResult>()

    suspend fun loadPage(query: String, pageIndex: Int, pageSize: Int, studentId: String? = null, classSectionId: String? = null, dateFrom: String? = null, dateTo: String? = null, sortBy: String = "created_at", order: String = "desc", refreshScope: CoroutineScope): Result<ClassConversationsPageResult> {
        val safeSize = pageSize.coerceIn(1, 100)
        val offset = pageIndex.coerceAtLeast(0) * safeSize
        scLog("$TAG loadPage start pageIndex=$pageIndex pageSize=$safeSize")
        val normalizedQuery = query.trim().takeIf { it.isNotBlank() }
        val normalizedStudentId = studentId?.trim()?.takeIf { it.isNotBlank() }
        val normalizedClassSectionId = classSectionId?.trim()?.takeIf { it.isNotBlank() }
        val normalizedDateFrom = dateFrom?.trim()?.takeIf { it.isNotBlank() }
        val normalizedDateTo = dateTo?.trim()?.takeIf { it.isNotBlank() }
        val key = tenantCacheKey("conversations:$normalizedQuery:$normalizedStudentId:$normalizedClassSectionId:$normalizedDateFrom:$normalizedDateTo:$offset:$safeSize:$sortBy:$order")
        return pageCache.load(refreshScope, TAG, "loadPage", key) { KtorApiClient.getClassConversations(
            query = normalizedQuery,
            classSectionId = normalizedClassSectionId,
            studentId = normalizedStudentId,
            dateFrom = normalizedDateFrom,
            dateTo = normalizedDateTo,
            limit = safeSize,
            offset = offset,
            sortBy = sortBy,
            order = order
        ).map { response ->
            ClassConversationsPageResult(response.items, response.count)
        }.onSuccess { result ->
            scLog("$TAG loadPage success pageIndex=$pageIndex count=${result.items.size} total=${result.totalCount}")
        }.onFailure { throwable ->
            scLog("$TAG loadPage failure pageIndex=$pageIndex", throwable)
        } }
    }

    suspend fun create(request: ClassConversationCreateRequest): Result<ClassConversationResponse> =
        KtorApiClient.createClassConversation(request)
            .onSuccess { pageCache.clear() }
            .onFailure { throwable -> scLog("$TAG create failure studentId=${request.studentId}", throwable) }

    suspend fun update(conversationId: String, title: String, description: String, attachmentUrl: String? = null, attachmentFilename: String? = null, attachmentSizeBytes: Int? = null, attachmentChanged: Boolean = false): Result<ClassConversationResponse> =
        KtorApiClient.updateClassConversation(conversationId, ClassConversationUpdateRequest(title = title, description = description, attachmentUrl = attachmentUrl, attachmentFilename = attachmentFilename, attachmentSizeBytes = attachmentSizeBytes), attachmentChanged)
            .onSuccess { pageCache.clear() }
            .onFailure { throwable -> scLog("$TAG update failure conversationId=$conversationId", throwable) }

    suspend fun loadDetail(conversationId: String): Result<ClassConversationResponse> =
        KtorApiClient.getClassConversation(conversationId)
            .onFailure { throwable -> scLog("$TAG loadDetail failure conversationId=$conversationId", throwable) }

    suspend fun loadReplies(conversationId: String): Result<List<ClassConversationReplyResponse>> =
        KtorApiClient.getClassConversationReplies(conversationId)
            .map(RecordListResponse<ClassConversationReplyResponse>::items)
            .onFailure { throwable -> scLog("$TAG loadReplies failure conversationId=$conversationId", throwable) }

    suspend fun createReply(conversationId: String, replyText: String, attachmentUrl: String? = null, attachmentFilename: String? = null): Result<ClassConversationReplyResponse> =
        KtorApiClient.createClassConversationReply(
            conversationId,
            ClassConversationReplyCreateRequest(replyText = replyText.trim(), attachmentUrl = attachmentUrl, attachmentFilename = attachmentFilename)
        ).onFailure { throwable -> scLog("$TAG createReply failure conversationId=$conversationId", throwable) }

    suspend fun moderate(conversationId: String, action: String, reason: String?): Result<ClassConversationResponse> =
        KtorApiClient.moderateClassConversation(conversationId, action, reason)
            .onFailure { throwable -> scLog("$TAG moderate failure conversationId=$conversationId action=$action", throwable) }

    suspend fun delete(conversationId: String, reason: String?): Result<Unit> =
        KtorApiClient.deleteClassConversation(conversationId, reason)
            .onSuccess { pageCache.clear() }
            .onFailure { throwable -> scLog("$TAG delete failure conversationId=$conversationId", throwable) }

    suspend fun deleteReply(conversationId: String, replyId: String, reason: String?): Result<Unit> =
        KtorApiClient.deleteClassConversationReply(conversationId, replyId, reason)
            .onFailure { throwable -> scLog("$TAG deleteReply failure conversationId=$conversationId replyId=$replyId", throwable) }
}
